import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';

import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from 'spotlight-ui/environments/environment';
import { NavigationService } from '../../navigation.service';
import { LoggedOutGuard } from '../../logged-out/logged-out.guard';
import { DebugLoggerService } from '../../core/debug-logger.service';

import { catchError, tap } from 'rxjs/operators';
import { Observable, throwError, EMPTY } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authInProgress: Promise<void> | null = null;

  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly logger: DebugLoggerService,
    private readonly router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.logger.info('[AuthInterceptor] Intercepting request:', request.url);
    return new Observable(observer => {
      this.handleAccess(request, next).then(result => {
        this.logger.info('[AuthInterceptor] handleAccess succeeded for:', request.url);
        result.subscribe(observer);
      }).catch(error => {
        this.logger.error('[AuthInterceptor] handleAccess failed for:', request.url, error);
        // Ensure error is an Error object before passing to observer
        const errorObj = error instanceof Error ? error : new Error(String(error));
        observer.error(errorObj);
      });
    });
  }

  private async handleAccess(request: HttpRequest<any>, next: HttpHandler): Promise<Observable<HttpEvent<any>>> {
    const allowedOrigins = environment.urlsForAuthHeader;
    const isAllowedOrigin = allowedOrigins.some(url => request.urlWithParams.includes(url));

    const serviceOrigins = environment.urlsForJWTHeader;
    const isServiceOrigin = serviceOrigins.some(url => request.urlWithParams.includes(url));
    this.logger.info('[AuthInterceptor] URL check:', { url: request.urlWithParams, isAllowedOrigin, allowedOrigins });

    if (isAllowedOrigin) {
      await this.waitForPendingAuthentication();
      const authResult = await this.checkAuthentication();

      if (!authResult.isAuthenticated) {
        return this.handleUnauthenticated();
      }

      request = this.addAuthToken(request, authResult.token);
    }

    if (isServiceOrigin) {
      await this.waitForPendingAuthentication();
      const authResult = await this.checkAuthentication();

      if (!authResult.isAuthenticated) {
        return this.handleUnauthenticated();
      }

      request = this.addJWTAuthToken(request, authResult.token);
    }

    return this.handleRequest(request, next);
  }

  private async waitForPendingAuthentication(): Promise<void> {
    if (this.authInProgress !== null) {
      this.logger.info('[AuthInterceptor] Waiting for in-progress authentication');
      await this.authInProgress;
      this.logger.info('[AuthInterceptor] In-progress authentication completed');
    }
  }

  private async checkAuthentication(): Promise<{ isAuthenticated: boolean; token: string | undefined }> {
    const token = this.oktaAuth.getAccessToken();
    this.logger.info('[AuthInterceptor] Token check:', { hasToken: !!token, tokenLength: token?.length });

    const isAuthenticated = await this.oktaAuth.isAuthenticated({ onExpiredToken: 'remove' });
    this.logger.info('[AuthInterceptor] Authentication status:', { isAuthenticated });

    return {
      isAuthenticated: !!(token && isAuthenticated),
      token
    };
  }

  private handleUnauthenticated(): Observable<HttpEvent<any>> {
    this.logger.info('[AuthInterceptor] User not authenticated or no token');

    // Check if we're already on the logged-out page
    const currentUrl = this.router.url;
    if (currentUrl.includes('/logged-out')) {
      this.logger.info('[AuthInterceptor] Already on logged-out page, skipping redirect');
      return EMPTY;
    }

    if (NavigationService.getIsLoggingOut()) {
      this.logger.info('[AuthInterceptor] Skipping redirect because user is logging out');
    } else {
      // Session expired - allow access to logged-out page and navigate
      this.logger.info('[AuthInterceptor] Session expired, navigating to logged-out page');
      LoggedOutGuard.setAllowAccess(true);
      this.router.navigate(['/logged-out']);
    }

    this.logger.info('[AuthInterceptor] Returning EMPTY observable, request cancelled');
    return EMPTY;
  }

  private addAuthToken(request: HttpRequest<any>, token: string | undefined): HttpRequest<any> {
    this.logger.info('[AuthInterceptor] User authenticated, adding bearer token to request');
    return request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + token,
      }
    });
  }

  private addJWTAuthToken(request: HttpRequest<any>, token: string | undefined): HttpRequest<any> {
    this.logger.info('[AuthInterceptor] User authenticated, adding bearer token to request');
    return request.clone({
      setHeaders: {
        Authorization: 'JWTBearer ' + token,
      }
    });
  }

  private handleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status === 200) {
          // If you want to take an action on a success response, do it here.
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.info('[AuthInterceptor] HTTP error caught:', { status: error.status, url: request.urlWithParams });
        const allowedOrigins = environment.urlsForAuthHeader;
        if (allowedOrigins.some(url => request.urlWithParams.includes(url))) {
          if (error.status === 403) {
            this.logger.info('[AuthInterceptor] 403 error, setting original URI:', globalThis.location.href);
            this.oktaAuth.setOriginalUri(globalThis.location.href);
          }
        }
        return throwError(() => error);
      })
    );
  }
}
